import { CategoryId } from "shared/hubrise/ids"
import { ICatalog, ICategory, ICategoryOrProduct, isCategory } from "shared/hubrise/models"
import {
  findById,
  getIds,
  insertItemInArray,
  moveItemWithinArray,
  removeInArrayById,
  replaceInArrayById,
} from "shared/utils/javascript"

import { updatedCatalog } from "./Catalog"
import * as Deal from "./Deal"
import { sortedCategoriesAndProducts, stableSortProductsByCategory } from "./Product"

/**
 * Add a category.
 */
export function add(
  catalog: ICatalog,
  insertAfter: ICategoryOrProduct | undefined,
  values: Partial<ICategory>,
): { newCatalog: ICatalog; category: ICategory } {
  let position = 0
  if (insertAfter) {
    if (isCategory(insertAfter)) {
      position = catalog.data.categories.findIndex(({ id }) => id === insertAfter.id) + 1
    } else {
      position = catalog.data.categories.findIndex(({ id }) => id === insertAfter.categoryId) + 1
    }
  }

  const id = CategoryId(catalog.idGenerator())
  const defaultValues: ICategory = {
    // Keep the order of the properties same as the interface for objectHash to generate consistent hashes.
    id,
    parentId: null,
    name: "",
    description: "",
    tags: [],
    imageHrIds: [],
  }
  const category: ICategory = { ...defaultValues, ...values }
  const categories: Array<ICategory> = insertItemInArray(catalog.data.categories, category, position)
  const newCatalog: ICatalog = updatedCatalog(catalog, { categories })
  return { newCatalog, category }
}

/**
 * Return the chain of ancestor categories of a category, from the direct parent up to the root.
 * @param catalog
 * @param category
 */
export const ancestors = (catalog: ICatalog, category: ICategory): ICategory[] => {
  const parentCategory = category.parentId !== null ? byId(catalog, category.parentId) : null
  if (parentCategory) {
    return [...ancestors(catalog, parentCategory), parentCategory]
  } else {
    return []
  }
}

/**
 * Return the category matching the id.
 * @param catalog
 * @param id
 */
export const byId = (catalog: ICatalog, id: CategoryId): ICategory => {
  const category = findById(catalog.data.categories, id)
  if (!category) throw `Category ${id} not found`
  return category
}

/**
 * Return the children categories of a category.
 * @param catalog
 * @param category
 */
export const children = (catalog: ICatalog, category: ICategory | null): Array<ICategory> =>
  catalog.data.categories.filter((c) => c.parentId === (category ? category.id : null))

/**
 * Return the descendant categories of a category.
 * @param catalog
 * @param category
 */
export const descendants = (catalog: ICatalog, category: ICategory | null): Array<ICategory> =>
  catalog.data.categories.filter((c) => category === null || isDescendant(catalog, c, category))

/**
 * Delete the category. The function name is because delete is a reserved JS word.
 * @param catalog
 * @param categoryId
 */
export function doDelete(catalog: ICatalog, category: ICategory): ICatalog {
  const categoriesToDelete = [category, ...descendants(catalog, category)]
  const categoryIdsToDelete = getIds(categoriesToDelete)
  const productsToDelete = catalog.data.products.filter(({ categoryId }) => categoryIdsToDelete.includes(categoryId))
  const skusToDelete = productsToDelete.flatMap((product) => product.skus)

  return updatedCatalog(catalog, {
    categories: catalog.data.categories.filter((category) => !categoriesToDelete.includes(category)),
    products: catalog.data.products.filter((product) => !productsToDelete.includes(product)),
    deals: Deal.deleteSkuIds(catalog, getIds(skusToDelete)).map((deal) =>
      Deal.detachFromCategory(deal, categoryIdsToDelete),
    ),
  })
}

/**
 * Tests whether `category` is a strict descendant of `ancestorCategory`.
 * @param catalog
 * @param category
 * @param ancestorCategory
 */
export const isDescendant = (catalog: ICatalog, category: ICategory, ancestorCategory: ICategory): boolean =>
  ancestors(catalog, category).includes(ancestorCategory)

/**
 * Move a category to a new position. Do nothing if both newParentCategoryId and insertAfterCategoryId are null.
 * @param catalog
 * @param category
 * @param newParentCategoryId The parent below which to insert the category. If null, move the category to top level.
 * @param insertAfterCategoryId The sibling category after which to insert the category. If null, insert as first child.
 */
export function move(
  catalog: ICatalog,
  category: ICategory,
  newParentCategoryId: CategoryId | null,
  insertAfterCategoryId: CategoryId | null,
): ICatalog {
  const categories = removeInArrayById(catalog.data.categories, category.id)

  let newIndex: number
  if (insertAfterCategoryId !== null) {
    newIndex = categories.findIndex((c) => c.id === insertAfterCategoryId) + 1
  } else if (newParentCategoryId !== null) {
    newIndex = categories.findIndex((c) => c.id === newParentCategoryId) + 1
  } else {
    newIndex = 0
  }

  const newCategories: Array<ICategory> = moveItemWithinArray(catalog.data.categories, category, newIndex, {
    ...category,
    parentId: newParentCategoryId,
  })

  return updatedCatalog(catalog, {
    categories: newCategories,
    products: stableSortProductsByCategory(newCategories, catalog.data.products),
  })
}

/**
 * Returns the category following the current product or category. Returns the first category if there is no current item.
 * The returned category is not a descendant of the current item (if the current item is a category).
 * @param catalog
 * @param categoryOrProduct
 */
export function next(catalog: ICatalog, categoryOrProduct?: ICategoryOrProduct): ICategory | undefined {
  const categoriesAndProducts = sortedCategoriesAndProducts(catalog)
  if (!categoryOrProduct) return categoriesAndProducts[0] as ICategory

  const position = categoriesAndProducts.indexOf(categoryOrProduct)
  if (position === undefined) return categoriesAndProducts[0] as ICategory

  const nextCategories = categoriesAndProducts.slice(position + 1).filter((cOrP) => isCategory(cOrP))
  if (isCategory(categoryOrProduct)) {
    return nextCategories.find((cOrP) => !isDescendant(catalog, cOrP, categoryOrProduct))
  } else {
    return nextCategories[0]
  }
}

/**
 * Update a category and return the resulting catalog.
 * @param catalog
 * @param categoryId
 * @param fieldName
 * @param value
 */
export function update<T extends keyof ICategory>(
  catalog: ICatalog,
  categoryId: CategoryId,
  fieldName: T,
  value: ICategory[T],
): ICatalog {
  return updatedCatalog(catalog, {
    categories: replaceInArrayById(catalog.data.categories, categoryId, (category) => ({
      ...category,
      [fieldName]: value,
    })),
  })
}

/**
 * Return top level categories.
 * @param catalog
 */
export const topLevel = (catalog: ICatalog): Array<ICategory> => children(catalog, null)

/**
 * Return the category's ref, which is the category's id as a string.
 * @param category
 */
export const ref = (category: ICategory): string => String(category.id)
